import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Inbox } from 'lucide-react';
import { useCurrentUser } from '../../features/auth/api';
import { useOrderNotesInbox, OrderNotesSort } from '../../features/order-notes/api';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import SearchInput from '../../components/SearchInput/SearchInput';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import './OrderNotesInboxPage.scss';

export default function OrderNotesInboxPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { data: me } = useCurrentUser();

  const [assignment, setAssignment] = useState<string>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [sort, setSort] = useState<OrderNotesSort>('recent');

  const search = useDebouncedSearch(searchInput, 350);

  useEffect(() => {
    if (!me) return;
    setAssignment(me.salesRepAddresses?.length ? 'ME' : 'ALL');
  }, [me]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useOrderNotesInbox({
    assignment,
    search,
    unseenOnly,
    sort,
  });

  const rows = data?.pages.flatMap((p) => p.rows) ?? [];

  const customerId = params.get('customerId');
  const filteredRows = customerId ? rows.filter((r) => r.customerId === customerId) : rows;

  return (
    <div className="layout">
      <AppSidebar />

      <div className="card order-notes-page">
        <div className="order-notes-page__header">
          <div>
            <h1 className="order-notes-page__title">Order Notes</h1>
            <p className="order-notes-page__subtitle">
              Review customer note threads by order and load number.
            </p>
          </div>

          <div className="order-notes-page__filters">
            <div className="form-control order-notes-page__filter order-notes-page__filter--search">
              <label>Search</label>
              <SearchInput
                value={searchInput}
                onValueChange={setSearchInput}
                placeholder="Customer or load #…"
              />
            </div>

            <div className="form-control order-notes-page__filter">
              <label>Sort</label>
              <select value={sort} onChange={(e) => setSort(e.target.value as OrderNotesSort)}>
                <option value="recent">Most recent notes</option>
                <option value="oldest">Oldest notes</option>
              </select>
            </div>

            <div className="form-control order-notes-page__filter">
              <label>Assignment</label>
              <select value={assignment} onChange={(e) => setAssignment(e.target.value)}>
                <option value="ALL">All</option>
                <option value="ME">My customers</option>
                <option value="UNASSIGNED">Unassigned</option>
              </select>
            </div>

            <button
              type="button"
              className={`order-notes-page__unopened${
                unseenOnly ? ' order-notes-page__unopened--active' : ''
              }`}
              aria-pressed={unseenOnly}
              onClick={() => setUnseenOnly((value) => !value)}
            >
              <Inbox size={16} />
              Unopened
            </button>
          </div>
        </div>

        {filteredRows.length === 0 ? (
          <div className="order-notes-empty-card">No note threads found.</div>
        ) : (
          <div className="order-notes-grid">
            {filteredRows.map((row) => (
              <button
                key={row.orderId}
                type="button"
                className="order-note-thread-card"
                onClick={() => navigate(`/order-notes/${row.orderId}`)}
              >
                <div className="order-note-thread-card__top">
                  <div>
                    <div className="order-note-thread-card__load-primary">
                      Load: {row.loadNumber || '—'}
                    </div>

                    <div className="order-note-thread-card__customer-secondary">
                      {row.customerName}
                    </div>
                  </div>

                  {row.unseenCount > 0 && (
                    <span className="order-note-thread-card__badge">{row.unseenCount} new</span>
                  )}
                </div>

                <div className="order-note-thread-card__preview">{row.latestNotePreview}</div>

                <div className="order-note-thread-card__date">
                  {new Date(row.latestNoteAt).toLocaleString()}
                </div>
              </button>
            ))}
            <div className="load-more">
              {hasNextPage && (
                <button
                  className="load-more__btn"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? <span className="load-more__spinner" /> : 'Load more'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
